import type { ZudokuConfig } from "zudoku";

import "./styles.css";

/**
 * The published API docs (docs.evaboot.com).
 *
 * The reference is generated: `apis/openapi.json` is written by
 * `manage.py export_openapi` and committed, so a change to the public API shows
 * up as a reviewable diff and CI fails when the two drift apart. Never hand-edit
 * it. Guide pages in `pages/` are written by hand.
 *
 * See docs/2026-08-14-api-docs-site.md.
 */
const config: ZudokuConfig = {
  site: {
    title: "Evaboot API",
    logo: {
      src: { light: "/logo-lockup.png", dark: "/logo-lockup-white.png" },
      alt: "Evaboot",
      width: "130px",
    },
    showPoweredBy: false,
  },
  // Without these the browser tab reads "<page> - Zudoku" with Zudoku's icon.
  metadata: {
    title: "%s | Evaboot Docs",
    defaultTitle: "Evaboot Docs",
    favicon: "/favicon.png",
  },
  // Evaboot purple (#581ADF, the logo colour). Dark mode uses a lighter tint
  // so links and the active nav item stay readable on a dark background.
  theme: {
    light: {
      primary: "#581ADF",
      primaryForeground: "#FFFFFF",
      accent: "#F3EEFE",
      accentForeground: "#3F12A6",
      ring: "#581ADF",
    },
    dark: {
      primary: "#A98BF5",
      primaryForeground: "#14072F",
      accent: "#251548",
      accentForeground: "#E4DAFD",
      ring: "#A98BF5",
    },
  },
  navigation: [
    {
      type: "category",
      label: "Documentation",
      // `index` must declare path "/" explicitly. Left as a bare string it
      // registers only /index, so the site root prerenders the right HTML and
      // then hydrates into the 404 route.
      items: [
        { type: "doc", file: "index", path: "/" },
        "mcp-server",
        "troubleshooting",
      ],
    },
    { type: "link", to: "/api", label: "API Reference" },
    {
      type: "category",
      label: "Use Cases",
      items: [
        {
          type: "doc",
          file: "use-cases/index",
          path: "/use-cases",
          label: "Overview",
        },
        "use-cases/lead-generation-agent",
        "use-cases/automated-lead-engine",
      ],
    },
  ],
  apis: [
    {
      type: "file",
      input: "./apis/openapi.json",
      path: "api",
      options: {
        expandAllTags: false,
      },
    },
  ],
  navigationRules: [
    {
      type: "modify",
      match: "API Reference/LinkedIn Extraction",
      set: { collapsed: false },
    },
  ],
  // Parity with the Redocly site we are replacing, which served per-page
  // markdown and LLM-friendly exports. All three are off by default, and the
  // llms flags nest under `docs.llms` -- set at the `docs` level they are
  // silently dropped and no llms.txt is emitted.
  docs: {
    publishMarkdown: true,
    llms: {
      llmsTxt: true,
      llmsTxtFull: true,
    },
  },
};

export default config;
